import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ConsultationForm from './components/ConsultationForm';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="w-full px-4 md:px-8 py-3 md:py-4">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-6 lg:gap-12">
              <button onClick={() => scrollToSection('capabilities')} className={`text-base lg:text-[21px] font-light transition-colors ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/60 hover:text-white'}`}>
                Solutions
              </button>
              <button onClick={() => scrollToSection('projects')} className={`text-base lg:text-[21px] font-light transition-colors ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/60 hover:text-white'}`}>
                Projects
              </button>
            </div>

            {/* Logo - Centered on Desktop, Left on Mobile */}
            <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 flex items-center gap-2 md:gap-3">
              <img src="https://public.readdy.ai/ai/img_res/d8a7fdc7-4864-47da-a009-f99d18a2e5cd.png" alt="Logo" className="h-[40px] w-[40px] md:h-[60px] md:w-[60px] object-contain" />
              <span className={`text-lg md:text-[30px] font-light tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>Intel Dynamics AI</span>
            </div>

            {/* CTA Button */}
            <div className="flex items-center gap-6">
              <ConsultationForm />
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button 
                onClick={() => scrollToSection('capabilities')} 
                className={`block w-full text-left px-4 py-2 rounded-lg ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
              >
                Solutions
              </button>
              <button 
                onClick={() => scrollToSection('projects')} 
                className={`block w-full text-left px-4 py-2 rounded-lg ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
              >
                Projects
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/899f52af27a97fdd245bd746dd20fcff.jpg" 
            alt="Enterprise Development Platform" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/10"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 max-w-7xl">
          <div className="w-full md:w-3/5 lg:w-2/5">
            <h1 className="text-4xl md:text-5xl lg:text-[70px] font-thin text-white leading-tight mb-6 md:mb-8">
              AI-Powered<br />
              Manufacturing<br />
              Intelligence
            </h1>
            <div className="space-y-4 md:space-y-6">
              <p className="text-base md:text-lg text-white/90 leading-relaxed">
                Transforming industrial operations through cutting-edge machine learning and artificial intelligence solutions
              </p>
              <p className="text-sm md:text-base text-white/80 leading-relaxed md:ml-32">
                Delivering measurable efficiency gains and competitive advantages to advanced manufacturing processes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="relative py-12 md:py-24 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-[55%] bg-white px-6 md:px-16 py-8 md:py-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-8 md:mb-16">
              Transforming Industrial<br />
              Manufacturing Through<br />
              Applied Intelligence
            </h2>
          </div>

          <div className="w-full md:w-[45%] relative bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-6 md:p-8 min-h-[300px] md:min-h-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Engineer%20using%20futuristic%20holographic%20interface%20to%20control%20two%20industrial%20robotic%20arms%20in%20bright%20modern%20manufacturing%20facility%2C%20transparent%20blue%20holographic%20displays%20floating%20in%20air%20showing%20robot%20control%20panels%20and%20data%20visualizations%2C%20hands%20gesturing%20through%20holographic%20screens%20like%20minority%20report%20style%20interaction%2C%20two%20large%20industrial%20robots%20visible%20in%20background%2C%20bright%20clean%20professional%20lighting%20with%20soft%20white%20and%20light%20blue%20ambient%20tones%2C%20high-tech%20workspace%20with%20minimalist%20aesthetic%20and%20advanced%20manufacturing%20equipment%2C%20professional%20photography%20quality%20emphasizing%20innovation%20and%20precision%20control&width=800&height=600&seq=capabilities-hologram-robots-001&orientation=landscape" 
              alt="Holographic Robot Control" 
              className="w-full h-full object-cover rounded-lg shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 mb-4 md:mb-6">
              <div className="w-3 h-3 bg-blue-900 transform rotate-45"></div>
              <span className="text-xs uppercase tracking-widest text-gray-600">CORE CAPABILITIES</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">Industrial AI Applications</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            {/* Card 1 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-blue-50 flex items-center justify-center p-6 md:p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20autonomous%20robotic%20system%20with%20AI%20neural%20network%20patterns%20and%20intelligent%20agent%20architecture%2C%20clean%20minimalist%20design%20with%20blue%20and%20white%20color%20scheme%2C%20showing%20interconnected%20robotic%20nodes%20and%20decision-making%20pathways%2C%20professional%20technical%20visualization%20for%20agentic%20AI%20robotics&width=400&height=400&seq=solution-agentic-robots-002&orientation=squarish" 
                  alt="Agentic AI Robotics" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Agentic AI Robotic Development</h3>
                <p className="text-sm text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Autonomous robotic systems powered by intelligent agents that make real-time decisions, adapt to changing conditions, and optimize manufacturing workflows
                </p>
                <button className="w-full md:w-3/4 mx-auto py-3 md:py-4 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                  Explore Solution
                </button>
                <p className="text-xs text-gray-400 mt-3 md:mt-4">Self-optimizing robotic intelligence</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-gray-50 flex items-center justify-center p-6 md:p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20industrial%20machine%20learning%20deployment%20with%20data%20pipelines%20and%20model%20training%20visualization%2C%20clean%20minimalist%20design%20with%20gray%20and%20white%20color%20scheme%2C%20showing%20practical%20AI%20implementation%20in%20manufacturing%20with%20analytics%20dashboards%20and%20production%20metrics%2C%20professional%20technical%20visualization%20for%20applied%20ML&width=400&height=400&seq=solution-practical-ml-002&orientation=squarish" 
                  alt="Industrial ML Development" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Practical Development and Application of Industrial Machine Learning and AI</h3>
                <p className="text-sm text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Custom machine learning models designed for real-world manufacturing challenges, from predictive analytics to quality control and process optimization
                </p>
                <button className="w-full md:w-3/4 mx-auto py-3 md:py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  Explore Solution
                </button>
                <p className="text-xs text-gray-400 mt-3 md:mt-4">Tailored AI solutions for production</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-blue-50 flex items-center justify-center p-6 md:p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20virtual%20reality%20and%20augmented%20reality%20manufacturing%20control%20interface%20with%20holographic%20displays%20and%20immersive%20visualization%2C%20clean%20minimalist%20design%20with%20blue%20and%20white%20color%20scheme%2C%20showing%20VR%2FAR%20headset%20interaction%20with%20industrial%20processes%20and%20real-time%20monitoring%20dashboards%2C%20professional%20technical%20visualization%20for%20immersive%20manufacturing%20control&width=400&height=400&seq=solution-vr-ar-control-002&orientation=squarish" 
                  alt="VR AR Manufacturing Control" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Virtual Reality and AR Control of Manufacturing Process</h3>
                <p className="text-sm text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Immersive VR and AR interfaces for intuitive control and monitoring of complex manufacturing systems, enabling remote operation and enhanced visualization
                </p>
                <button className="w-full md:w-3/4 mx-auto py-3 md:py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  Explore Solution
                </button>
                <p className="text-xs text-gray-400 mt-3 md:mt-4">Next-generation manufacturing interfaces</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Card 4 - Digital Twin */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-gray-50 flex items-center justify-center p-6 md:p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20digital%20twin%20technology%20showing%20virtual%20replica%20of%20industrial%20manufacturing%20system%20with%20real-time%20data%20synchronization%20and%203D%20modeling%2C%20clean%20minimalist%20design%20with%20gray%20and%20white%20color%20scheme%2C%20displaying%20parallel%20physical%20and%20digital%20representations%20connected%20by%20data%20streams%20and%20IoT%20sensors%2C%20professional%20technical%20visualization%20for%20digital%20twin%20development&width=400&height=400&seq=solution-digital-twin-001&orientation=squarish" 
                  alt="Digital Twin Development" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Digital Twin Development</h3>
                <p className="text-sm text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Create virtual replicas of physical manufacturing systems for real-time monitoring, predictive analysis, and scenario testing without disrupting production operations
                </p>
                <button className="w-full md:w-3/4 mx-auto py-3 md:py-4 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                  Explore Solution
                </button>
                <p className="text-xs text-gray-400 mt-3 md:mt-4">Virtual-physical system integration</p>
              </div>
            </div>

            {/* Card 5 - Process Simulation */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-48 md:h-64 bg-blue-50 flex items-center justify-center p-6 md:p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20technical%20illustration%20of%20manufacturing%20process%20simulation%20with%20workflow%20optimization%20and%20production%20line%20modeling%2C%20clean%20minimalist%20design%20with%20blue%20and%20white%20color%20scheme%2C%20showing%20simulation%20software%20interface%20with%20process%20flow%20diagrams%20and%20performance%20analytics%2C%20professional%20technical%20visualization%20for%20manufacturing%20simulation%20and%20optimization&width=400&height=400&seq=solution-process-simulation-001&orientation=squarish" 
                  alt="Process and Manufacturing Simulation" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Process and Manufacturing Simulation</h3>
                <p className="text-sm text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Advanced simulation tools to model production workflows, test process changes, optimize resource allocation, and validate improvements before implementation
                </p>
                <button className="w-full md:w-3/4 mx-auto py-3 md:py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  Explore Solution
                </button>
                <p className="text-xs text-gray-400 mt-3 md:mt-4">Risk-free process optimization</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="flex flex-col md:flex-row items-start justify-between mb-8 md:mb-12 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
                Featured <span className="text-blue-600">Implementations</span>
              </h2>
              <p className="text-sm text-gray-600">Real-world applications delivering measurable results</p>
            </div>
            <div className="px-4 py-2 bg-gray-800 text-white text-xs rounded-full">CASE STUDIES</div>
          </div>

          <div className="flex gap-6 md:gap-8 overflow-x-auto pb-4 scrollbar-hide">
            {[
              {
                image: 'https://readdy.ai/api/search-image?query=Industrial%20Yaskawa%20style%20robotic%20arm%20performing%20precision%20welding%20on%20automotive%20assembly%20line%20with%20bright%20welding%20sparks%20and%20orange%20glow%2C%20high-speed%20manufacturing%20environment%20with%20yellow%20and%20black%20industrial%20robots%2C%20professional%20factory%20photography%20showing%20advanced%20automation%20welding%20process%2C%20dynamic%20action%20shot%20with%20motion%20and%20energy%20in%20modern%20manufacturing%20facility&width=400&height=400&seq=project-welding-robots-001&orientation=squarish',
                industry: 'Automotive Manufacturing',
                application: 'Quality Assurance System',
                metric: '34% Reduction',
                detail: 'in defect rates'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Multiple%20Yaskawa%20style%20industrial%20robotic%20arms%20performing%20high-speed%20precision%20assembly%20of%20electronic%20components%20on%20production%20line%2C%20yellow%20and%20black%20robots%20working%20in%20coordinated%20motion%2C%20professional%20factory%20photography%20showing%20advanced%20automated%20assembly%20process%20with%20small%20parts%20and%20circuit%20boards%2C%20clean%20well-lit%20manufacturing%20environment%20with%20blue%20accent%20lighting&width=400&height=400&seq=project-assembly-robots-001&orientation=squarish',
                industry: 'Electronics Production',
                application: 'Predictive Maintenance',
                metric: '52% Decrease',
                detail: 'in downtime'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Industrial%20Yaskawa%20style%20robotic%20arm%20with%20vision%20system%20performing%20automated%20quality%20inspection%20of%20aerospace%20metal%20components%2C%20yellow%20and%20black%20robot%20with%20camera%20and%20sensors%20examining%20precision%20machined%20parts%2C%20professional%20factory%20photography%20showing%20advanced%20inspection%20technology%20with%20measurement%20displays%20and%20data%20visualization%2C%20high-tech%20manufacturing%20environment%20with%20professional%20lighting&width=400&height=400&seq=project-inspection-robots-001&orientation=squarish',
                industry: 'Aerospace Components',
                application: 'Process Optimization',
                metric: '28% Increase',
                detail: 'in throughput'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Yaskawa%20style%20industrial%20robots%20performing%20automated%20packaging%20and%20material%20handling%20in%20pharmaceutical%20clean%20room%2C%20yellow%20and%20black%20robotic%20arms%20working%20with%20sterile%20products%20and%20containers%2C%20professional%20factory%20photography%20showing%20high-speed%20automated%20packaging%20process%20in%20white%20clean%20room%20environment%20with%20blue%20lighting%20and%20quality%20control%20systems&width=400&height=400&seq=project-pharma-robots-001&orientation=squarish',
                industry: 'Pharmaceutical Manufacturing',
                application: 'Quality Control AI',
                metric: '99.8% Accuracy',
                detail: 'detection rate'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=Multiple%20Yaskawa%20style%20industrial%20robotic%20arms%20performing%20high-speed%20sorting%20and%20palletizing%20of%20food%20products%20on%20production%20line%2C%20yellow%20and%20black%20robots%20handling%20packages%20and%20containers%20with%20precision%2C%20professional%20factory%20photography%20showing%20advanced%20automated%20food%20processing%20with%20stainless%20steel%20equipment%20and%20conveyor%20systems%20in%20clean%20manufacturing%20environment&width=400&height=400&seq=project-food-robots-001&orientation=squarish',
                industry: 'Food Processing',
                application: 'Supply Chain Optimization',
                metric: '41% Improvement',
                detail: 'in efficiency'
              }
            ].map((project, index) => (
              <div key={index} className="flex-shrink-0 w-72 md:w-80 bg-gray-50 rounded-2xl overflow-hidden">
                <div className="w-72 h-72 md:w-80 md:h-80">
                  <img src={project.image} alt={project.industry} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-xs text-gray-600 mb-3 md:mb-4 leading-relaxed">
                    {project.industry}<br />{project.application}
                  </p>
                  <div className="text-right">
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{project.metric}</div>
                    <div className="text-sm text-gray-600">{project.detail}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative h-[70vh] md:h-screen w-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Advanced%20manufacturing%20floor%20with%20robotic%20assembly%20lines%20and%20precision%20equipment%20in%20operation%2C%20high-tech%20clean%20room%20with%20professional%20lighting%20showcasing%20cutting-edge%20industrial%20automation%2C%20wide%20angle%20view%20of%20modern%20factory%20with%20warm%20industrial%20tones%20and%20sophisticated%20machinery&width=1920&height=1080&seq=cta-manufacturing-001&orientation=landscape" 
            alt="Manufacturing Excellence" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white uppercase tracking-wider mb-6 md:mb-10 leading-tight">
            READY TO TRANSFORM<br />YOUR MANUFACTURING?
          </h2>
          <p className="text-base md:text-lg text-white mb-8 md:mb-14 leading-relaxed">
            Schedule a consultation with our AI engineering team<br className="hidden md:block" />
            Discover custom solutions for your production challenges
          </p>
          <button className="relative inline-flex items-center gap-3 md:gap-4 bg-gray-900 text-white px-8 md:px-12 py-4 md:py-5 rounded-full text-xs md:text-sm uppercase tracking-widest font-medium hover:bg-gray-800 transition-all group whitespace-nowrap cursor-pointer">
            <div className="absolute -left-1 w-12 h-12 md:w-14 md:h-14 bg-orange-500 rounded-full flex items-center justify-center">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-share-forward-line text-white text-lg md:text-xl"></i>
              </div>
            </div>
            <span className="ml-10 md:ml-12">START YOUR PROJECT</span>
            <i className="ri-arrow-right-up-line text-base md:text-lg group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 rounded-t-3xl mx-3 md:mx-6 mt-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 relative z-10">
            <div className="md:col-span-6">
              <h3 className="text-2xl md:text-3xl font-light text-white leading-tight mb-8 md:mb-12">
                Let's Build<br />The Future
              </h3>
            </div>

            <div className="md:col-span-6">
              <h4 className="text-base font-medium text-white mb-4 md:mb-6">Solutions</h4>
              <ul className="space-y-2 md:space-y-3">
                {[
                  'Agentic AI Robotic Development',
                  'Industrial Machine Learning and AI',
                  'Virtual Reality and AR Control',
                  'Digital Twin Development',
                  'Process and Manufacturing Simulation'
                ].map((item, index) => (
                  <li key={index}>
                    <a href="#" className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-500 text-center md:text-left">© 2025 Intel Dynamics AI. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-white transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>

          <div className="hidden md:block absolute bottom-8 left-1/2 transform -translate-x-1/2 text-6xl lg:text-9xl font-thin text-white/5 pointer-events-none whitespace-nowrap">
            INTEL DYNAMICS
          </div>
        </div>
      </footer>
    </div>
  );
}